import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { createPortal } from "react-dom";
import { currentDomain, currentPath } from "../Index";
import { usePromotionsData } from "../data/PromotionData";
import Deposit from "../transactions/Deposit";
import LoginGlobal from "../data/LoginGlobal";
import { useNavigate } from "react-router-dom";
import useAppPromotionTheme from "../hooks/useAppPromotionTheme";

export default function WeeklyCashback({ isLightMode }) {
	const isAppPromo = useAppPromotionTheme(isLightMode);
	const { promotionsData, isPromotionDataReady } = usePromotionsData();
	const data = useMemo(
		() => promotionsData.find((promo) => promo.title === "Weekly Cashback"),
		[promotionsData],
	);
	const [isMobile, setIsMobile] = useState(false);
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [isDepositOpen, setIsDepositOpen] = useState(false);
	const navigate = useNavigate();

	useEffect(() => {
		const loggedInState = localStorage.getItem("isLoggedIn") === "true";
		setIsLoggedIn(loggedInState);
	}, []);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 841);
		};
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		if (!isDepositOpen) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, [isDepositOpen]);

	// Table Data Incase
	const tableData = [
		{
			vipTier: "Alexandrite",
			bonus: "7%",
		},
		{
			vipTier: "Diamond",
			bonus: "6%",
		},
		{
			vipTier: "Ruby",
			bonus: "5%",
		},
		{
			vipTier: "Sapphire",
			bonus: "4%",
		},
		{
			vipTier: "Emerald",
			bonus: "3%",
		},
	];

	// If the promo page is expired - throws error
	useEffect(() => {
		if (!isPromotionDataReady) return;

		if (!data) {
			navigate("/not-found");
		}
	}, [isPromotionDataReady, data, navigate]);

	if (!isPromotionDataReady || !data) return null;

	return (
		<>
			<Helmet>
				<meta charSet="utf-8" />
				<title>Weekly Cashback</title>
				<link rel="icon" href="/images/fav.webp" sizes="32x32" />
				<meta name="description" content="ddd" />
				<link rel="canonical" href={`${currentDomain}${currentPath}`} />
			</Helmet>
			<div className="promotion-page-main all-page-main-container-min-height">
				<div className="promotion-page-container main-container-global-adjusted-padding">
					<div className="promotion-tnc-page">
						<div className="promotion-page-breadcrumb">
							<a
								href={
									isAppPromo
										? "/promotion/".replace("/promotion", "/app-promotion")
										: "/promotion/"
								}
							>
								Promotions{" "}
							</a>{" "}
							/ <span>{data.title}</span>
						</div>
						<div className="promotion-tnc-banner">
							<img
								src={isMobile ? data.imgSrc : data.bannerSrc}
								alt={`FGH ${data.title}`}
							/>
						</div>
						<div className="promotion-tnc-content">
							<div className="promotion-tnc-info">
								<div
									className={`promotion-indication-message ${data.promotionMessage.class}`}
								>
									{data.promotionMessage.text}
								</div>
								<h1>Weekly Cashback</h1>
								<h2>Up to 7% cashback</h2>
								<div className="promotion-tnc-period">
									<strong>Promo Period: </strong>
									<span>Weekly Promotion</span>
								</div>
							</div>
							<div className="promotion-tnc-mechanic-content">
								<div className="table-wrapper">
									<table
										className="promotion-tnc-table"
										style={{ minWidth: "0px" }}
									>
										<thead>
											<th>VIP Tier</th>
											<th>Bonus</th>
										</thead>
										<tbody>
											{tableData.map((row, index) => (
												<tr key={index}>
													<td>{row.vipTier}</td>
													<td>{row.bonus}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>

								<strong>Terms & Conditions:</strong>
								<ol>
									<li>Promo is valid from Monday 12pm to Monday 12pm.</li>
									<li>
										A minimum net loss of 5,000 in the casino qualifies for a
										cashback
									</li>
									<ul>
										<li>
											Net loss means Total Deposit less Total Withdrawal and
											Total Bonuses:
										</li>
									</ul>
									<p>Example 1</p>
									<ul>
										<li>Total Deposit : PHP 10,000</li>
										<li>Total Bonuses : PHP 2,000</li>
										<li>Total Withdrawal : PHP 3,000</li>
										<li>Net Loss : PHP 5,000 (qualified for cashback)</li>
									</ul>
									<p>Example 2</p>
									<ul>
										<li>Total Deposit : PHP 10,000</li>
										<li>Total Bonuses : PHP 2,000</li>
										<li>Total Withdrawal : PHP 5,000</li>
										<li>Net Loss : PHP 3,000 (NOT qualified for cashback)</li>
									</ul>
									<li>
										Players will receive between 3-7% cashback based on their
										VIP tier during the campaign period depending on how much
										net losses.
									</li>
									<li>There is no maximum cashback to be paid out.</li>
									<li>
										Players will be automatically credited on every Monday 3pm
										latest.
									</li>
									<li>
										Cashback is credited as bonus money and needs to be wagered
										1X before withdrawal.
									</li>
									<li>
										<p>
											<strong className="highlight">
												{" "}
												Unused Bonus Reset Policy:
											</strong>
										</p>
										<p>
											<span className="promotion-tnc-block-text unused-bonus-reset">
												&bull; Once the bonus is credited to the player’s
												account, it must be used before{" "}
												<strong className="highlight">
													{" "}
													11:00 AM on the first day of the following month.{" "}
												</strong>{" "}
												Any unused bonus balance after this period will
												automatically reset and be forfeited.
											</span>
										</p>
									</li>
									<li>
										<p>
											FGH reserves the right to change, suspend or cancel the
											promotion at any time.
										</p>
									</li>
								</ol>
								{!isAppPromo && (
									<>
										<div className="promotion-tnc-cta">
											{isLoggedIn ? (
												<button
													type="button"
													className="promotion-cta-btn"
													style={{ cursor: "pointer" }}
													onClick={() => setIsDepositOpen(true)}
												>
													Get a Cashback Now
												</button>
											) : (
												<LoginGlobal
													label="Get a Cashback Now"
													btnClass="promotion-cta-btn"
													onlyBtn={true}
												/>
											)}
										</div>
										{isDepositOpen &&
											createPortal(
												<div className="modal-portal">
													<Deposit
														onClose={() => setIsDepositOpen(false)}
														defaultBonusName="New Member Package Bonus"
													/>
												</div>,
												document.body,
											)}
									</>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
